import React, { useMemo, useRef, useState } from "react";

import {

  Box,

  Typography,

  TextField,

  Button,

  Stack,

  Avatar,

  IconButton,

  alpha,

  useTheme,

  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,

} from "@mui/material";

import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useTranslation } from "react-i18next";

import { getInitials } from "../../../utils/utils";
import { useTeamBoardAction } from "../hooks/useTeamBoardAction";
import TaskCommentComposer, { renderCommentBody } from "./TaskCommentComposer";
import TeamMessageAttachment from "../../Team/components/TeamMessageAttachment";
import { normalizeTaskComment } from "../utils/taskCommentMedia";
import { formatDeletedUserName } from "../utils/teamBoardCardFilters";
import { buildTaskCommentSendItems } from "../utils/taskCommentSend";
import {
  buildPayloadFromUploadedFile,
  uploadTeamFile,
} from "../../Team/utils/teamAttachmentSend";
import TaskActivityLogLine from "./TaskActivityLogLine";



function formatCommentTime(iso) {

  if (!iso) return "";

  try {

    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) return "";

    return d.toLocaleString("pt-BR", {

      day: "2-digit",

      month: "short",

      hour: "2-digit",

      minute: "2-digit",

    });

  } catch {

    return "";

  }

}



const TaskCardComments = ({
  comments = [],
  cardId,
  currentUserId,
  mutations,
  users = [],
  boardMembers = [],
  board = null,
}) => {

  const { t } = useTranslation("teamTasks");

  const theme = useTheme();

  const { run } = useTeamBoardAction();

  const listEndRef = useRef(null);

  const [draft, setDraft] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const [editingId, setEditingId] = useState(null);

  const [editDraft, setEditDraft] = useState("");

  const [savingEditId, setSavingEditId] = useState(null);

  const [deletingId, setDeletingId] = useState(null);

  const isClientBoard = Boolean(board?.clientId);
  const [commentVisibility, setCommentVisibility] = useState("internal");

  const sortedComments = useMemo(

    () => [...comments].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),

    [comments]

  );



  const scrollToBottom = () => {

    requestAnimationFrame(() => {

      listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });

    });

  };



  const handleSubmit = async ({ body, mentions, files = [] }) => {
    const items = buildTaskCommentSendItems({ body, mentions, files });
    if (!items.length || !mutations?.createComment || submitting) return false;

    setSubmitting(true);
    try {
      let lastResult = null;
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        const result = await run(
          async () => {
            let payload = { body: item.body, mentions: item.mentions };
            if (item.file) {
              const uploaded = await uploadTeamFile(item.file);
              const media = buildPayloadFromUploadedFile(item.file, uploaded.url, item.body);
              payload = {
                body: item.body,
                mentions: item.mentions,
                messageType: media.messageType,
                imageUrl: media.imageUrl,
                videoUrl: media.videoUrl,
                audioUrl: media.audioUrl,
                fileUrl: media.fileUrl,
                fileName: media.fileName || item.file?.name || null,
              };
            }
            if (isClientBoard) {
              payload.visibility = commentVisibility === "client" ? "client" : "internal";
            }
            return mutations.createComment.mutateAsync({ cardId, ...payload });
          },
          {
            context: "comment",
            successKey: index === items.length - 1 ? "successCommentSent" : undefined,
          }
        );
        if (result === null) return null;
        lastResult = result;
      }

      setDraft("");
      scrollToBottom();
      return lastResult;
    } finally {
      setSubmitting(false);
    }
  };



  const handleStartEdit = (comment) => {

    setEditingId(comment.id);

    setEditDraft(comment.body || "");

  };



  const handleSaveEdit = async (commentId) => {

    const body = editDraft.trim();

    if (!body || !mutations?.updateComment || savingEditId) return;

    setSavingEditId(commentId);

    try {

      const result = await run(

        () => mutations.updateComment.mutateAsync({ cardId, commentId, body }),

        { context: "comment" }

      );

      if (result === null) return;

      setEditingId(null);

      setEditDraft("");

    } finally {

      setSavingEditId(null);

    }

  };



  const handleDelete = async (commentId) => {

    if (!window.confirm(t("deleteCommentConfirm"))) return;

    if (!mutations?.deleteComment || deletingId) return;

    setDeletingId(commentId);

    try {

      await run(

        () => mutations.deleteComment.mutateAsync({ cardId, commentId }),

        { context: "comment" }

      );

    } finally {

      setDeletingId(null);

    }

  };



  return (

    <Box sx={{ mt: 1 }}>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>

        <ChatBubbleOutlineIcon fontSize="small" color="action" />

        <Typography variant="subtitle2" fontWeight={700}>

          {t("commentsSection")}

        </Typography>

        {sortedComments.length > 0 ? (

          <Typography variant="caption" color="text.secondary">

            ({sortedComments.length})

          </Typography>

        ) : null}

      </Stack>



      <Stack spacing={1.5} sx={{ mb: 2 }}>

        {sortedComments.map((comment) => {

          const isLog = String(comment?.type || "").toLowerCase() === "log";

          if (isLog) {
            return (
              <TaskActivityLogLine
                key={comment.id}
                text={comment.body || comment.displayBody || ""}
                actorName={formatDeletedUserName(comment.authorName || "", comment.authorDeleted, t("deletedUserMark"))}
                createdAt={comment.createdAt}
              />
            );
          }

          const mediaComment = normalizeTaskComment(comment);

          const isOwn = currentUserId != null && Number(comment.authorId) === Number(currentUserId);

          const isEditing = editingId === comment.id;

          const authorLabel =
            formatDeletedUserName(comment.authorName, comment.authorDeleted, t("deletedUserMark")) ||
            t("commentUnknownAuthor");

          const canEditText = isOwn && !mediaComment.hasMedia;



          return (

            <Stack key={comment.id} direction="row" spacing={1.25} alignItems="flex-start">

              <Avatar

                src={comment.authorAvatarUrl || undefined}

                sx={{

                  width: 32,

                  height: 32,

                  fontSize: "0.75rem",

                  bgcolor: "primary.main",

                  flexShrink: 0,

                }}

              >

                {getInitials(authorLabel)}

              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>

                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>

                  <Typography variant="body2" fontWeight={700}>

                    {authorLabel}

                  </Typography>

                  <Typography variant="caption" color="text.secondary">

                    {formatCommentTime(comment.createdAt)}

                  </Typography>

                  {comment.editedAt ? (

                    <Typography variant="caption" color="text.disabled">

                      ({t("commentEdited")})

                    </Typography>

                  ) : null}

                </Stack>



                {isEditing ? (

                  <Box sx={{ mt: 0.75 }}>

                    <TextField

                      value={editDraft}

                      onChange={(e) => setEditDraft(e.target.value)}

                      fullWidth

                      multiline

                      minRows={2}

                      size="small"

                      autoFocus

                      disabled={savingEditId === comment.id}

                    />

                    <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>

                      <Button

                        size="small"

                        variant="contained"

                        onClick={() => handleSaveEdit(comment.id)}

                        disabled={!editDraft.trim() || savingEditId === comment.id}

                        startIcon={

                          savingEditId === comment.id ? (

                            <CircularProgress size={14} color="inherit" />

                          ) : null

                        }

                        sx={{ fontWeight: 600 }}

                      >

                        {savingEditId === comment.id ? t("saving") : t("save")}

                      </Button>

                      <Button

                        size="small"

                        onClick={() => {

                          setEditingId(null);

                          setEditDraft("");

                        }}

                        disabled={savingEditId === comment.id}

                      >

                        {t("cancel")}

                      </Button>

                    </Stack>

                  </Box>

                ) : (
                  <>
                    {mediaComment.displayBody ? (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.35,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          lineHeight: 1.55,
                          opacity: deletingId === comment.id ? 0.5 : 1,
                        }}
                      >
                        {renderCommentBody(mediaComment.displayBody, theme)}
                      </Typography>
                    ) : null}
                    {mediaComment.hasMedia ? (
                      <Box
                        sx={{
                          mt: mediaComment.displayBody ? 1 : 0.75,
                          maxWidth: 360,
                          opacity: deletingId === comment.id ? 0.5 : 1,
                        }}
                      >
                        <TeamMessageAttachment
                          message={{
                            messageType: mediaComment.messageType,
                            imageUrl: mediaComment.imageUrl,
                            videoUrl: mediaComment.videoUrl,
                            audioUrl: mediaComment.audioUrl,
                            fileUrl: mediaComment.fileUrl,
                            fileName: mediaComment.fileName,
                          }}
                        />
                      </Box>
                    ) : !mediaComment.displayBody && comment.body ? (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.35,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          lineHeight: 1.55,
                        }}
                      >
                        {renderCommentBody(comment.body, theme)}
                      </Typography>
                    ) : null}
                  </>
                )}



                {isOwn && !isEditing ? (

                  <Stack direction="row" spacing={0.25} sx={{ mt: 0.35 }}>

                    {canEditText ? (
                    <IconButton

                      size="small"

                      onClick={() => handleStartEdit(comment)}

                      disabled={Boolean(deletingId || savingEditId)}

                      aria-label={t("editComment")}

                    >

                      <EditOutlinedIcon sx={{ fontSize: 16 }} />

                    </IconButton>
                    ) : null}

                    <IconButton

                      size="small"

                      onClick={() => handleDelete(comment.id)}

                      disabled={Boolean(deletingId || savingEditId)}

                      aria-label={t("deleteComment")}

                    >

                      {deletingId === comment.id ? (

                        <CircularProgress size={14} />

                      ) : (

                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />

                      )}

                    </IconButton>

                  </Stack>

                ) : null}

              </Box>

            </Stack>

          );

        })}

        <span ref={listEndRef} />

      </Stack>



      <Box

        sx={{

          p: 1.5,

          borderRadius: 2,

          bgcolor: alpha(theme.palette.background.default, 0.7),

          border: `1px solid ${theme.palette.divider}`,

        }}

      >

        {isClientBoard ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={commentVisibility}
            onChange={(_e, value) => {
              if (value) setCommentVisibility(value);
            }}
            sx={{ mb: 1 }}
          >
            <ToggleButton value="internal">Interno</ToggleButton>
            <ToggleButton value="client">Visível ao cliente</ToggleButton>
          </ToggleButtonGroup>
        ) : null}

        <TaskCommentComposer
          value={draft}
          onChange={setDraft}
          onSubmit={handleSubmit}
          submitting={submitting}
          users={users}
          boardMembers={boardMembers}
          board={board}
          currentUserId={currentUserId}
        />

      </Box>

    </Box>

  );

};



export default TaskCardComments;

